import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import ContactSection from "@/components/ContactSection";
import HeyflowBanner from "@/components/HeyflowBanner";
import ProvenExpertBadge from "@/components/ProvenExpertBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Shield, 
  Home, 
  TrendingUp, 
  Heart, 
  CheckCircle, 
  Phone,
  Mail,
  Calculator,
  Users,
  Award,
  Clock,
  ArrowLeft
} from "lucide-react";
import janineHardiImage from "@/assets/janine-hardi.jpg";
import leibrenteImage from "@/assets/leibrente.png";
import { Link } from "react-router-dom";

const Leibrente = () => {

  const benefits = [
    {
      icon: Shield,
      title: "Lebenslange Zahlung",
      description: "Garantierte monatliche Rente bis zum Lebensende"
    },
    {
      icon: Home,
      title: "Wohnrecht bleibt",
      description: "Lebenslang in der vertrauten Immobilie wohnen"
    },
    {
      icon: TrendingUp,
      title: "Keine Erbschaftssteuer",
      description: "Steuerliche Vorteile durch frühzeitige Übertragung"
    },
    {
      icon: Heart,
      title: "Mehr Lebensqualität",
      description: "Finanzielle Sicherheit für Ihre Wünsche"
    }
  ];

  const examples = [
    {
      title: "Einfamilienhaus München",
      value: "1.200.000 €",
      age: "78 Jahre",
      rent: "2.500 €",
      details: "Wohnrecht lebenslang, keine Erbschaftssteuer"
    },
    {
      title: "Eigentumswohnung Köln", 
      value: "500.000 €",
      age: "80 Jahre",
      rent: "1.200 €",
      details: "Einmalzahlung möglich: 50.000 €"
    },
    {
      title: "Haus im ländlichen Raum",
      value: "300.000 €", 
      age: "76 Jahre",
      rent: "800 €",
      details: "Finanzierung von Pflege- und Reisekosten"
    }
  ];

  const faqItems = [
    {
      question: "Was passiert, wenn ich früher versterbe?",
      answer: "In diesem Fall profitiert der Käufer – das ist der 'Preis' für die Sicherheit der lebenslangen Zahlung. Es können aber Vertragsoptionen vereinbart werden, bei denen Erben eine Mindestlaufzeit garantiert bekommen."
    },
    {
      question: "Ist die Leibrente sicher?",
      answer: "Ja, das Wohnrecht wird im Grundbuch eingetragen und die Zahlungen sind vertraglich gesichert."
    },
    {
      question: "Was, wenn ich ins Pflegeheim ziehe?",
      answer: "Das Wohnrecht kann in ein Vermietungsrecht umgewandelt werden – Sie erhalten weiterhin Ihre Rente."
    },
    {
      question: "Bekommen meine Kinder etwas vererbt?",
      answer: "Die Immobilie selbst nicht mehr, aber das zusätzliche Einkommen kann genutzt werden, um den Kindern oder Enkeln zu helfen."
    },
    {
      question: "Muss ich Steuern auf die Rente zahlen?",
      answer: "Ja, aber nur auf den sogenannten Ertragsanteil – dieser hängt vom Alter ab und ist in der Regel relativ gering."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Leibrente: Haus verkaufen & wohnen bleiben </title>
        <meta 
          name="description" 
          content="Leibrente: Haus verkaufen, wohnen bleiben & lebenslange Rente erhalten. ✓ Wohnrecht ✓ Monatliche Zahlung ✓ Kostenlose Beratung zur Seniorenfinanzierung"
        />
        <meta name="keywords" content="leibrente, haus verrenten, wohnrecht, seniorenfinanzierung, haus verkaufen und wohnen bleiben, immobilie verrenten, auszahlung haus rentner, eigenheimverrentung" />
        <link rel="canonical" href="https://eigenheimverrentung.de/leibrente" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Leibrente: Haus verkaufen & wohnen bleiben" />
        <meta property="og:description" content="Verkaufen Sie Ihr Haus, bleiben Sie wohnen und erhalten eine lebenslange monatliche Rente. Kostenlose Beratung zur Leibrente." />
        <meta property="og:url" content="https://eigenheimverrentung.de/leibrente" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Leibrente: Haus verkaufen & wohnen bleiben" />
        <meta name="twitter:description" content="Verkaufen Sie Ihr Haus, bleiben Sie wohnen und erhalten eine lebenslange monatliche Rente." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Leibrente – Haus verkaufen und wohnen bleiben mit lebenslanger Rente",
            "description": "Kompletter Ratgeber zur Leibrente: Verkaufen Sie Ihr Haus, bleiben wohnen und erhalten lebenslange monatliche Rentenzahlungen.",
            "author": {
              "@type": "Organization",
              "name": "eigenheimverrentung.de"
            },
            "publisher": {
              "@type": "Organization", 
              "name": "eigenheimverrentung.de",
              "url": "https://eigenheimverrentung.de"
            },
            "datePublished": new Date().toISOString(),
            "dateModified": new Date().toISOString(),
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://eigenheimverrentung.de/leibrente"
            }
          })}
        </script>
        
        {/* FAQ Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Was passiert, wenn ich früher versterbe?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "In diesem Fall profitiert der Käufer – das ist der 'Preis' für die Sicherheit der lebenslangen Zahlung. Es können aber Vertragsoptionen vereinbart werden, bei denen Erben eine Mindestlaufzeit garantiert bekommen."
                }
              },
              {
                "@type": "Question",
                "name": "Ist die Leibrente sicher?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, das Wohnrecht wird im Grundbuch eingetragen und die Zahlungen sind vertraglich gesichert."
                }
              },
              {
                "@type": "Question",
                "name": "Was, wenn ich ins Pflegeheim ziehe?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Das Wohnrecht kann in ein Vermietungsrecht umgewandelt werden – Sie erhalten weiterhin Ihre Rente."
                }
              },
              {
                "@type": "Question",
                "name": "Bekommen meine Kinder etwas vererbt?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Die Immobilie selbst nicht mehr, aber das zusätzliche Einkommen kann genutzt werden, um den Kindern oder Enkeln zu helfen."
                }
              },
              {
                "@type": "Question",
                "name": "Muss ich Steuern auf die Rente zahlen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, aber nur auf den sogenannten Ertragsanteil – dieser hängt vom Alter ab und ist in der Regel relativ gering."
                }
              }
            ]
          })}
        </script>

        {/* Organization & Local Business Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "RentePlusImmobilie - Eigenheimverrentung",
            "url": "https://eigenheimverrentung.de",
            "telephone": "+49-89-628246446",
            "email": "info@renteplusimmobilie.de",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "DE"
            },
            "areaServed": "Deutschland",
            "serviceType": ["Leibrente", "Wohnrecht", "Nießbrauchrecht", "Teilverkauf", "Rückmiete", "Seniorenfinanzierung"],
            "description": "Kostenlose Beratung zur Eigenheimverrentung mit Leibrente, Wohnrecht, Nießbrauchrecht, Teilverkauf und mehr."
          })}
        </script>

        {/* BreadcrumbList Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Startseite",
                "item": "https://eigenheimverrentung.de/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Leibrente",
                "item": "https://eigenheimverrentung.de/leibrente"
              }
            ]
          })}
        </script>

        {/* HowTo Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Wie beantrage ich eine Leibrente?",
            "description": "Schritt-für-Schritt Anleitung zur Beantragung einer Leibrente bei renteplusimmobilie.de",
            "totalTime": "P14D",
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": "Kostenlose Erstberatung",
                "text": "Unverbindliche Beratung zu allen Modellen der Immobilienverrentung"
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": "Wertermittlung",
                "text": "Professionelle Bewertung unter Berücksichtigung von Lage, Größe und Marktpreisen"
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Angebotsvergleich",
                "text": "Prüfung und Gegenüberstellung seriöser Angebote verschiedener Anbieter"
              },
              {
                "@type": "HowToStep",
                "position": 4,
                "name": "Vertragsabschluss",
                "text": "Notarielle Beurkundung, Grundbucheintrag und Auszahlung der Leibrente"
              }
            ]
          })}
        </script>

        {/* Service Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Leibrente",
            "name": "Leibrente - Lebenslange finanzielle Sicherheit",
            "description": "Verkaufen Sie Ihr Haus, bleiben Sie wohnen und erhalten eine lebenslange monatliche Rente. Wohnrecht wird im Grundbuch eingetragen.",
            "provider": {
              "@type": "LocalBusiness",
              "name": "RentePlusImmobilie - Eigenheimverrentung",
              "url": "https://eigenheimverrentung.de",
              "telephone": "+49-89-628246446",
              "email": "info@renteplusimmobilie.de"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Deutschland"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Senioren und Immobilieneigentümer ab 65 Jahren"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Leibrente Leistungen",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Kostenlose Erstberatung",
                    "description": "Unverbindliche Beratung zu allen Modellen der Immobilienverrentung"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Professionelle Immobilienbewertung",
                    "description": "Wertermittlung unter Berücksichtigung von Lage, Größe und Marktpreisen"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Angebotsvergleich",
                    "description": "Prüfung und Gegenüberstellung seriöser Angebote verschiedener Anbieter"
                  }
                }
              ]
            },
            "url": "https://eigenheimverrentung.de/leibrente"
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-background/70">
      
      <Header />
      
      <main>
        {/* Breadcrumb Navigation */}
        <section className="py-4 bg-muted/30 border-b">
          <div className="container-comfortable">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">
                Startseite
              </Link>
              <span>/</span>
              <span className="text-primary font-medium">Leibrente</span>
            </nav>
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-subtle">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="secondary" className="mb-6 text-base px-4 py-2">
                Kostenlose Beratung
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold text-primary mb-6 text-balance">
                Leibrente – Lebenslange finanzielle Sicherheit
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-4 text-balance">
                Monatliche Rente bis zum Lebensende bei Verbleib in der Immobilie
              </p>
              <p className="text-base text-muted-foreground mb-8 max-w-3xl mx-auto">
                Die Leibrente ist die beste Lösung für Senioren ab 75 Jahren, die ihr Haus verkaufen und trotzdem wohnen bleiben möchten. Sie erhalten eine lebenslange monatliche Rente – z. B. 2.500 € bei einem Immobilienwert von 1.200.000 €. Das Wohnrecht wird im Grundbuch eingetragen und ist damit rechtlich vollständig abgesichert.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-xl px-8" onClick={() => window.open('https://calendly.com/renteplusimmobilie/20min', '_blank')}>
                  <Calculator className="h-5 w-5 mr-2" />
                  Kostenloses Telefongespräch
                </Button>
                <Button variant="outline" size="lg" className="text-xl px-8" onClick={() => window.open('tel:089628246446', '_self')}>
                  <Phone className="h-5 w-5 mr-2" />
                  089 62 82 46 46
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Ihre Vorteile der Leibrente
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-2">
                Die Leibrente bietet Ihnen die perfekte Kombination aus finanzieller Sicherheit und dem Verbleib in Ihrem gewohnten Zuhause.
              </p>
              <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                Lebenslange Zahlung, grundbuchlich gesichertes Wohnrecht, keine Erbschaftssteuer und mehr Lebensqualität – diese 4 Kernvorteile machen die Leibrente zur sichersten Form der Eigenheimverrentung.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="card-elevated text-center">
                  <CardHeader className="pb-4">
                    <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                      <benefit.icon className="h-8 w-8 text-accent" />
                    </div>
                    <CardTitle className="text-xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Image Section */}
        <section className="py-12 md:py-16">
          <div className="container-comfortable">
            <div className="max-w-2xl mx-auto text-center">
              <img 
                src={leibrenteImage} 
                alt="Leibrente - Haus verkaufen und wohnen bleiben mit lebenslanger Rente" 
                className="w-full max-w-md mx-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* What is Leibrente Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
                Was ist eine Leibrente?
              </h2>
              
              <div className="prose prose-lg max-w-none text-muted-foreground">
                <p className="text-xl leading-relaxed mb-6">
                  Die Leibrente ist eine besondere Form der Immobilienverrentung. Dabei verkauft der Eigentümer seine Immobilie an einen Käufer – häufig institutionelle Investoren oder spezialisierte Unternehmen – und erhält im Gegenzug eine lebenslange monatliche Rentenzahlung.
                </p>
                
                <div className="bg-card p-6 rounded-xl border mb-8">
                  <h3 className="text-2xl font-bold text-primary mb-4">Das Besondere an der Leibrente:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                      <span>Der Verkäufer behält ein lebenslanges Wohnrecht oder sogar ein Nießbrauchrecht, das im Grundbuch abgesichert wird</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                      <span>Die Höhe der monatlichen Rente hängt vom Wert der Immobilie, dem Alter des Verkäufers und seiner statistischen Lebenserwartung ab</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                      <span>Zusätzlich können Einmalzahlungen vereinbart werden</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Example Calculations */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Beispielrechnungen & Praxisfälle
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Konkrete Beispiele zeigen, wie sich die Leibrente in verschiedenen Situationen auswirkt
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {examples.map((example, index) => (
                <Card key={index} className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-xl text-primary">{example.title}</CardTitle>
                    <div className="text-3xl font-bold text-accent">{example.value}</div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Alter:</span>
                      <span className="font-semibold">{example.age}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Monatliche Rente:</span>
                      <span className="font-bold text-2xl text-primary">{example.rent}</span>
                    </div>
                    <Separator />
                    <p className="text-sm text-muted-foreground">{example.details}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Der Ablauf bei renteplusimmobilie.de
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                In 4 einfachen Schritten zu Ihrer Leibrente
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Users className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">1. Kostenlose Erstberatung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Unverbindliche Beratung zu allen Modellen der Immobilienverrentung</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Calculator className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">2. Wertermittlung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Professionelle Bewertung unter Berücksichtigung von Lage, Größe und Marktpreisen</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Award className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">3. Individuelle Vertragsgestaltung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Maßgeschneiderte Lösung mit optimaler Rentenhöhe und Wohnrechtssicherung</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Clock className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">4. Auszahlung & Sicherheit</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Pünktliche monatliche Auszahlung mit lebenslang gesichertem Wohnrecht</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Häufige Fragen zur Leibrente
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Die wichtigsten Antworten zu Ihren Fragen rund um die Leibrente
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-6">
              {faqItems.map((item, index) => (
                <Card key={index} className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-xl text-primary">{item.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              {/* Founder Image */}
              <div className="mb-8">
                <img 
                  src={janineHardiImage} 
                  alt="Janine Hardi - Gründerin von RentePlusImmobilie" 
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover mx-auto shadow-lg"
                />
                <p className="text-sm text-muted-foreground mt-3">Janine Hardi, Gründerin von RentePlusImmobilie</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4 text-center">
                Warum RentePlusImmobilie
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Ihr vertrauensvoller Partner für professionelle Immobilienverrentung
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="card-elevated text-center">
                <CardHeader className="pb-4">
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Users className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">Kostenlose Beratung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Unverbindliche Erstberatung zu allen Verrentungsoptionen</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader className="pb-4">
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Calculator className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">Faire Bewertung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Professionelle Immobilienbewertung für optimale Ergebnisse</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader className="pb-4">
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Award className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">Erfahrung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Langjährige Expertise in der Immobilienverrentung</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader className="pb-4">
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Shield className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl">Sicherheit</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Rechtlich abgesicherte Verträge und vertrauensvolle Abwicklung</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <ProvenExpertBadge />
      <ContactSection />
      <HeyflowBanner />
      </div>
    </>
  );
};

export default Leibrente;